import React from "react"
import { FilterSelector } from "@components"
import { Button } from "@heroui/react"

import { 
  ArrowPathIcon,
  ChevronDoubleLeftIcon, 
  ChevronDoubleRightIcon, 
  ChevronLeftIcon, 
  ChevronRightIcon 
} from "@heroicons/react/20/solid"

import PropTypes from "prop-types"
import AvailabilityModeToggle from "./AvailabilityModeToggle"

const filters = [
  {
    section: "Slot Status",
    items: {
      available: { id: "available", name: "Available", dotColor: "available" },
      booked: { id: "booked", name: "Booked", dotColor: "booking" },
      disabled: { id: "disabled", name: "Disabled", dotColor: "disabled" },
    }
  },
  {
    section: "Attendance Status",
    items: {
      attended: { id: "attended", name: "Attended", dotColor: "attended" },
      absent: { id: "absent", name: "Absent", dotColor: "absent" },
      unknown: { id: "unknown", name: "Unknown", dotColor: "unknown" },
    }
  }
]

const Controls = ({ 
  handleCancelAvailabilityChanges,
  handleFirstPage, 
  handleLastPage,
  handleNextPage, 
  handlePreviousPage, 
  handleRefresh,
  handleSaveAvailabilityChanges,
  handleToggleFilter,
  handleToggleMode,
  isReadOnlyMode,
  isRefetching = false,
  mode,
  noOfAvailabilityChanges = 0,
  page, 
  selectedFilters,
  totalPages
}) => (
  <div className="flex items-center justify-between px-4 pt-3">
    <div className="px-8 py-2 font-semibold text-white bg-indigo-500 rounded-lg">Page {page} of {totalPages}</div>
    
    <div className="flex items-center gap-1">
      <Button className="text-white" isIconOnly color="secondary" variant="solid" onPress={() => handleFirstPage()}>
        <ChevronDoubleLeftIcon className="h-7" />
      </Button>
      
      <Button className="text-white" isIconOnly color="secondary" variant="solid"  onPress={() => handlePreviousPage()}>
        <ChevronLeftIcon className="h-7" />
      </Button>

      <Button className="text-white" isIconOnly color="secondary" variant="solid" onPress={() => handleNextPage()}>
        <ChevronRightIcon className="h-7" />
      </Button>
      
      <Button className="mr-6 text-white" isIconOnly color="secondary" variant="solid" onPress={() => handleLastPage()}>
        <ChevronDoubleRightIcon className="h-7" />
      </Button>

      {noOfAvailabilityChanges > 0 && mode === "availability" && (
        <div className="flex items-center gap-1">
          <Button className="font-semibold text-white" color="success" onPress={handleSaveAvailabilityChanges}>Save {noOfAvailabilityChanges} Change{noOfAvailabilityChanges > 1 ? "s" : ""}</Button>
          <Button className="text-white" color="warning" onPress={handleCancelAvailabilityChanges}>Cancel</Button>
        </div>
      )}
      
      {noOfAvailabilityChanges === 0 && !isReadOnlyMode && (
        <AvailabilityModeToggle mode={mode} handleModeChange={handleToggleMode} />
      )}
      
      <FilterSelector
        closeOnSelect={true}
        color="primary"
        disallowEmptySelection={false}
        filters={filters}
        selectionMode="single"
        selectedFilters={[...selectedFilters]}
        showFilterCount={true}
        toggleFilter={selectedFilters => handleToggleFilter(selectedFilters)}
        variant="solid"
      />

      <Button className="text-white" isIconOnly color="primary" variant="solid"  onPress={() => handleRefresh()} isLoading={isRefetching}>
        <ArrowPathIcon className="h-7" />
      </Button>
    </div>      
  </div>
)

Controls.propTypes = {
  handleFirstPage: PropTypes.func.isRequired,
  handlePreviousPage: PropTypes.func.isRequired,
  handleNextPage: PropTypes.func.isRequired,
  handleLastPage: PropTypes.func.isRequired,
  handleRefresh: PropTypes.func.isRequired,
  handleToggleFilter: PropTypes.func.isRequired,
  handleToggleMode: PropTypes.func.isRequired,
  isReadOnlyMode: PropTypes.bool.isRequired,
  isRefetching: PropTypes.bool,
  noOfAvailabilityChanges: PropTypes.number,
  mode: PropTypes.string.isRequired,
  page: PropTypes.number,
  selectedFilters: PropTypes.object.isRequired,
  totalPages: PropTypes.number,
}

export default React.memo(Controls, (prev, next) => {
  return (
    prev.page === next.page &&
    prev.totalPages === next.totalPages &&
    prev.isReadOnlyMode === next.isReadOnlyMode &&
    prev.isRefetching === next.isRefetching &&
    prev.mode === next.mode &&
    prev.noOfAvailabilityChanges === next.noOfAvailabilityChanges &&
    Array.from(prev.selectedFilters).join(",") === Array.from(next.selectedFilters).join(",")
  )
});